import Button from '@admin/components/button'
import Format from '@admin/components/format'
import Icon from '@admin/components/icon'
import Token from '@admin/tokens/token'
import T from '@admin/components/t'
import PropTypes from 'prop-types'
import React from 'react'

class Dropdown extends React.Component {

  static propTypes = {
    defaultValue: PropTypes.any,
    deselectable: PropTypes.bool,
    direction: PropTypes.string,
    disabled: PropTypes.bool,
    entity: PropTypes.string,
    format: PropTypes.any,
    id: PropTypes.string,
    label: PropTypes.string,
    nullable: PropTypes.bool,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    selectFirst: PropTypes.bool,
    tabIndex: PropTypes.number,
    textKey: PropTypes.string,
    valueKey: PropTypes.string,
    value: PropTypes.any,
    onBusy: PropTypes.func,
    onChange: PropTypes.func,
    onReady: PropTypes.func,
    onValid: PropTypes.func
  }

  static defaultProps = {
    deselectable: true,
    format: Token,
    nullable: false,
    options: [],
    selectFirst: false,
    tabIndex: 0,
    textKey: 'text',
    valueKey: 'value',
    onBusy: () => {},
    onChange: () => {},
    onReady: () => {},
    onValid: () => {}
  }

  controlRef = React.createRef()
  optionRefs = []

  state = {
    animating: false,
    active: false,
    direction: null,
    selected: null,
    value: null,
    focusedIndex: null
  }

  _handleBlur = this._handleBlur.bind(this)
  _handleClickOutside = this._handleClickOutside.bind(this)
  _handleOpen = this._handleOpen.bind(this)  
  _handleSetSelected = this._handleSetSelected.bind(this)
  _handleValidate = this._handleValidate.bind(this)
  _handleFocus = this._handleFocus.bind(this)
  _handleKeyDown = this._handleKeyDown.bind(this)

  render() {
    const { deselectable, format, nullable, options } = this.props
    const { selected } = this.state
    this.optionRefs = []
    return (
      <div { ...this._getDropdown() } >
        <div className="maha-input-field">
          <div { ...this._getDropdownClassProps() }>
            <div onClick={ this._handleOpen }>
              { this._getLabel() }
            </div>
            <div { ...this._getMenu() }>
              { nullable &&
                <div key="nullable_option" { ...this._getNullableOption() }>
                  <div className="token">&nbsp;</div>
                </div>
              }
              { options.map((option, index) => (
                <div key={`option_${index}`} { ...this._getOption(index) }>
                  <Format { ...option } option={ option } format={ format } value={ this._getText(option) } />
                </div>
              )) }
            </div>
          </div>
        </div>
        { deselectable && selected !== null ?
          <Button { ...this._getDeselect() } /> :
          <div className="maha-input-action" onClick={ this._handleOpen }>
            <Icon svg="chevron_down" />
          </div>
        }
      </div>
    )
  }

  componentDidMount() {
    const defaultValue = this._getDefaultValue()
    if(!_.isNil(defaultValue)) this._handleSet(defaultValue)
    document.addEventListener('mousedown', this._handleClickOutside)
    this.props.onReady(this._handleValidate)
  }

  componentDidUpdate(prevProps, prevState) {
    const { value } = this.props
    const { active, selected } = this.state
    if(selected !== prevState.selected) {
      this._handleChange()
    }
    if(!_.isEqual(value, prevProps.value)) {
      this._handleSet(value)
    }
    if(active !== prevState.active) {
      this._handleAnimate()
    }
    this._handleScrollIntoView()
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this._handleClickOutside)
  }

  _getClass() {
    const classes = ['maha-dropdown','maha-input']
    return classes.join(' ')
  }

  _getDefaultValue() {
    const { defaultValue, value, selectFirst, valueKey } = this.props
    const { options } = this.props
    const first = options.length > 0 ? _.get(options[0], valueKey) : null
    return !_.isNil(value) ? value : !_.isNil(defaultValue) ? defaultValue : (selectFirst ? first : null)
  }

  _getIndex(value) {
    const { options, valueKey } = this.props
    const index = options.findIndex(option => {
      const optionValue = valueKey ? _.get(option, valueKey) : option
      return _.isEqual(optionValue, value)
    })
    return index >= 0 ? index : null
  }

  _getDeselect() {
    return {
      svg: 'x',
      className: 'maha-input-action',
      handler: this._handleChoose.bind(this, null)
    }
  }

  _getDropdown() {
    const { tabIndex } = this.props
    const classes = ['maha-dropdown','maha-input']
    return {
      className: classes.join(' '),
      tabIndex,
      ref: this.controlRef,
      onFocus: this._handleFocus,
      onKeyDown: this._handleKeyDown,
      onBlur: this._handleBlur
    }
  }

  _getDropdownClass() {
    const { animating, active, direction } = this.state
    const { disabled } = this.props
    const classes = ['ui','fluid','selection','dropdown']
    if(disabled) classes.push('disabled')
    if(direction) classes.push(direction)
    if(active) classes.push('active')
    if(active && !animating) classes.push('visible')
    if(!active && animating) classes.push('visible')
    return classes.join(' ')
  }

  _getDropdownClassProps() {
    return {
      className: this._getDropdownClass(),
      onClick: this._handleOpen
    }
  }

  _getLabel() {
    const { entity, format, label, options, placeholder, textKey } = this.props
    const { selected } = this.state
    const option = selected !== null ? options[selected] : null
    if(option && format) return <Format { ...option } option={ option } format={ format } value={ this._getText(option) } />
    const text = option ? _.get(option, textKey) : placeholder || `t(Choose) ${label || entity}`
    return <div className="placeholder"><T text={ text } /></div>
  }

  _getMenu() {
    return {
      className: this._getMenuClass()
    }
  }

  _getMenuClass() {
    const { active, animating } = this.state
    const classes = ['menu','transition']
    const direction = this.state.direction === 'upward' ? 'up' : 'down'
    if(!animating && !active) classes.push('hidden')
    if(animating || active) classes.push('visible')
    if(animating && active) classes.push(`animating slide ${direction} in`)
    if(animating && !active) classes.push(`animating slide ${direction} out`)
    return classes.join(' ')
  }

  _getNullableOption() {
    const { active, focusedIndex } = this.state
    return {
      className: 'item' + (focusedIndex === 0 ? ' focused' : ''),
      onClick: this._handleChoose.bind(this, null),
      ref: el => { if (active) this.optionRefs[0] = el }
    }
  }

  _getOption(index) {
    const { nullable } = this.props
    const { active, focusedIndex } = this.state
    const optIndex = nullable ? index + 1 : index
    return {
      className: 'item' + (focusedIndex === optIndex ? ' focused' : ''),
      onClick: this._handleChoose.bind(this, index),
      ref: el => { if (active) this.optionRefs[optIndex] = el }
    }
  }

  _getScrollContainer(node) {
    const parent = node.parentNode
    if(node.tagName === 'BODY') return null
    const style = getComputedStyle(parent)
    return style['overflow-y'] === 'auto' ? parent : this._getScrollContainer(parent)
  }

  _getText(option) {
    const { textKey } = this.props
    return textKey ? _.get(option, textKey) : option
  }

  _getValue(index) {
    if(index === null) return null
    if(index < 0) return null
    const { options, valueKey } = this.props
    const option = options[index]
    return valueKey ? _.get(option, valueKey) : option
  }

  _handleAnimate() {
    this.setState({
      animating: true
    })
    setTimeout(() => this.setState({
      animating: false
    }), 250)
  }

  _handleBlur(e) {
    if(!this.state.active) return
    this.setState({ active: false, focusedIndex: null })
  }

  _handleChange() {
    const { selected } = this.state
    const value = this._getValue(selected)
    this.props.onChange(value)
  }

  _handleChoose(index, e) {
    e.stopPropagation()
    this._handleSetSelected(index)
    this.setState({
      active: false
    })  
  }

  _handleClickOutside(e) {
    const { active } = this.state
    if(!active) return
    const parent = e.target.closest('.maha-dropdown')
    if(parent !== null && parent.isEqualNode(this.controlRef.current)) return
    this.setState({
      active: false
    })
  }

  _handleFocus(e) {
    if (!this.state.active && this.state.focusedIndex !== null) {
      this.setState({ focusedIndex: null })
    }
  }

  _handleKeyDown(e) {
    const { options, nullable, disabled } = this.props
    const { active, focusedIndex, selected } = this.state
    if (disabled) return
    const totalOptions = options.length + (nullable ? 1 : 0)
    // Open on arrow if closed
    if (!active && ['ArrowDown','ArrowUp'].includes(e.key)) {
      e.preventDefault()
      let newFocusedIndex = 0
      if (selected !== null && selected !== undefined) {
        newFocusedIndex = nullable ? selected + 1 : selected
      }
      this.setState({ active: true, focusedIndex: newFocusedIndex })
      return
    }
    // Open on Enter/Spacebar if closed
    if (!active && ['Enter',' ','Spacebar'].includes(e.key)) {
      e.preventDefault()
      let newFocusedIndex = 0
      if (selected !== null && selected !== undefined) {
        newFocusedIndex = nullable ? selected + 1 : selected
      }
      this.setState({ active: true, focusedIndex: newFocusedIndex })
      return
    }
    if (!active) return
    if (['ArrowDown','ArrowUp'].includes(e.key)) {
      e.preventDefault()
      let nextIndex
      if (focusedIndex === null) {
        nextIndex = 0
      } else if (e.key === 'ArrowDown') {
        nextIndex = (focusedIndex + 1) % totalOptions
      } else if (e.key === 'ArrowUp') {
        nextIndex = (focusedIndex - 1 + totalOptions) % totalOptions
      }
      this.setState({ focusedIndex: nextIndex })
      return
    }
    if (['Enter',' ','Spacebar'].includes(e.key)) {
      e.preventDefault()
      // Select the focused option
      if (focusedIndex !== null) {
        if (nullable && focusedIndex === 0) {
          this._handleSetSelected(null)
        } else {
          const optIndex = nullable ? focusedIndex - 1 : focusedIndex
          this._handleSetSelected(optIndex)
        }
        this.setState({ active: false, focusedIndex: null })
      }
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      this.setState({ active: false, focusedIndex: null })
      return
    }
  }

  _handleOpen(e) {
    const { direction, disabled } = this.props
    const { active } = this.state
    const container = this._getScrollContainer(this.controlRef.current)
    if(!container) return this.setState({ direction: null, active: true })
    const rect = container.getBoundingClientRect()
    const percent = ((e.clientY - rect.y) / rect.height) * 100
    if(disabled || active || e.target.className === 'item') return
    this.setState({
      direction: direction || (percent > 75 ? 'upward' : null),
      active: true
    })
  }

  _handleScrollIntoView() {
    const { active } = this.state
    if (active && this.state.focusedIndex !== null && this.optionRefs[this.state.focusedIndex]) {
      this.optionRefs[this.state.focusedIndex].scrollIntoView({ block: 'nearest' })
    }
  }

  _handleSet(value) {
    const index = this._getIndex(value)
    this._handleSetSelected(index)
  }

  _handleSetSelected(selected) {
    this.setState({ selected })
  }

  _handleValidate() {
    const { selected } = this.state
    const { required } = this.props
    if(required && selected === null) return this.props.onValid(null, ['t(You must choose an option)'])
    const value = this._getValue(selected)
    this.props.onValid(value)
  }

}

export default Dropdown
